import { lang } from "../../i18n/i18n";
import raw from "./catalog.json";

/** Catalogue des annotations APP-6 (conception du 15/10/2026), jumeau client de `generex_engine/annotations.py` :
 *  `catalog.json` est la copie exacte du catalogue du serveur (`tools/annotations/render_svgs.mjs`), et `sidcOf`
 *  calcule le même SIDC que le serveur (vecteurs partagés `server/tests/fixtures/annotations_vectors.json`). Le SIDC
 *  n'est jamais saisi : clé, identité et échelon le donnent, APP-6D version 10, contexte réalité 0. Libellés FR et EN
 *  (OTAN britannique) dans le catalogue même. */

export type AnnotationCategoryKey = "units" | "population" | "infrastructure" | "control" | "events";

export type AnnotationField =
  | "label"
  | "identity"
  | "echelon"
  | "strength"
  | "equipment"
  | "source"
  | "phases"
  | "notes"
  | "attitude"
  | "community"
  | "state"
  | "protected"
  | "capacity"
  | "held_by"
  | "instructions"
  | "dtg"
  | "toll";

export interface AnnotationCategory {
  key: AnnotationCategoryKey;
  letter: string;
  fr: string;
  en: string;
  identities: number[];
  default_identity: number;
  fields: AnnotationField[];
}

export interface AnnotationSymbol {
  key: string;
  category: AnnotationCategoryKey;
  fr: string;
  en: string;
  set: string;
  entity: string;
  m1: string;
  m2: string;
  default_identity: number;
  identities: number[];
  /** Sigle de numérotation automatique (« CKP 1 »). */
  short?: string;
  /** Unité de l'ensemble 10 : l'échelon estimé entre dans le SIDC. */
  echelon?: boolean;
  /** Site protégé par défaut (hôpital, lieu de culte, école). */
  protected?: boolean;
  /** Amplificateur texte porté à côté du symbole (« PRESSE »). */
  text?: { fr: string; en: string };
  /** Obstacle sans symbole milsymbol (barrage routier) : dessin propre. */
  obstacle?: boolean;
  svg?: string;
  anchor?: [number, number];
}

interface Labelled {
  fr: string;
  en: string;
}

export const CATALOG = raw as unknown as {
  schema: number;
  version: string;
  categories: AnnotationCategory[];
  identities: ({ code: number } & Labelled)[];
  echelons: ({ code: string } & Labelled)[];
  symbols: AnnotationSymbol[];
};

export const CATEGORIES: readonly AnnotationCategory[] = CATALOG.categories;
export const SYMBOLS: ReadonlyMap<string, AnnotationSymbol> = new Map(CATALOG.symbols.map((s) => [s.key, s]));
const ECHELON_CODES = new Set(CATALOG.echelons.map((e) => e.code));

/** Libellé dans la langue de l'interface (OTAN britannique en anglais). */
export function labelled(entry: Labelled): string {
  return lang() === "en" ? entry.en : entry.fr;
}

export function symbolOf(key: string): AnnotationSymbol | undefined {
  return SYMBOLS.get(key);
}

export function categoryOf(key: string): AnnotationCategory | undefined {
  const symbol = SYMBOLS.get(key);
  return symbol ? CATEGORIES.find((c) => c.key === symbol.category) : undefined;
}

export function symbolsOf(category: AnnotationCategoryKey): AnnotationSymbol[] {
  return CATALOG.symbols.filter((s) => s.category === category);
}

/** SIDC à 20 chiffres : identité choisie (sinon celle par défaut), échelon des unités de l'ensemble 10 seulement. */
export function sidcOf(key: string, identity?: number | null, echelon?: string | null): string {
  const symbol = SYMBOLS.get(key);
  if (!symbol) throw new Error(`Annotation inconnue : ${key}`);
  const ident = identity ?? symbol.default_identity;
  const level = symbol.echelon && echelon && ECHELON_CODES.has(echelon) ? echelon : "00";
  return `100${ident}${symbol.set}00${level}${symbol.entity}${symbol.m1}${symbol.m2}`;
}

export function identityName(code: number): string {
  const entry = CATALOG.identities.find((i) => i.code === code);
  return entry ? labelled(entry) : String(code);
}

export function echelonLabel(code: string): string {
  const entry = CATALOG.echelons.find((e) => e.code === code);
  return entry ? labelled(entry) : code;
}

/** Champs de la fiche d'un symbole (ceux de sa catégorie). */
export function fieldsOf(key: string): readonly AnnotationField[] {
  return categoryOf(key)?.fields ?? [];
}
